// Core lib dependencies.
import React from 'react';
import { connect } from 'react-redux';
import { Modal } from 'react-overlays';
import PropTypes from 'prop-types';

// Component dependencies.
import { Button, HybridButton } from '../../../../common/components/buttons.jsx';

// Actions.
import Actions from '../actions.js';

// Selectors.
import Selectors from '../selectors.js';

// Config.
import Config from '../config/config.js';

/**
 * <pre>
 * Overlay React component.
 *
 * Summary of responsibilities:
 *
 *  - smart component (connected to Redux store).
 *  - composes elements required to output an Overlay.
 *  - receives child components as props and renders inside Overlay container.
 *  - handles positioning of Overlay in page/frame as required.
 *
 * FYI:
 *
 *  this component uses '{@link https://github.com/react-bootstrap/react-overlays|React-overlays}' library (Utilities for creating robust overlay components).
 * </pre>
 *
 * @see {@link https://github.com/react-bootstrap/react-overlays|React-overlays}
 *
 * @author Tim Loram
 *
 * @docPreview {namedExport: "docPreviewExport"}
 *
 * @memberof module:Overlay.Components
 * @namespace
 *
 * @return {module:Overlay.Components.Overlay}
 */
class Overlay extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);

    // Initial state.
    const instanceId = this.props.totalInstances + 1;
    this.state = {
      instanceId: instanceId,
    };

    // Bind methods.
    this.center = this.center.bind(this);
    this.handleCloseClick = this.handleCloseClick.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleInnerClick = this.handleInnerClick.bind(this);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Lifecycle methods */

  /**
   * Called when component mounts for the first time.
   * Dispatches action to open and center overlay.
   */
  componentDidMount() {
    // Dispatch action to open overlay.
    this.props.dispatch.open(this.state.instanceId);

    // Dispatch action to center overlay.
    this.center();

    // Listen for external changes to overlay content outside of Reacts control.
    const eventService = NGXD.getServiceContainer().getService('event');
    eventService.listen('overlay:content:rendered', () => {
      this.center();
    });
    eventService.listen('parent:event:click', this.handleClose);
  }

  componentWillUnmount() {
    const eventService = NGXD.getServiceContainer().getService('event');
    eventService.unlisten('parent:event:click', this.handleClose);

    const apiService = NGXD.getServiceContainer().getService('api');
    apiService.execute('resizeParent');
  }

  /**
   * Called whenever component is re-rendered.
   * Dispatches action to center overlay.
   */
  componentDidUpdate() {
    // Dispatch action to center overlay.
    this.center();
  }

  shouldComponentUpdate(nextProps, nextState) {
    const instance = this.props.instances[this.state.instanceId];
    const nextInstance = nextProps.instances[this.state.instanceId];

    // If we have a dynamic content flag `hasDynamicContent` set, then we will always re-render for state changes,
    // which follows the default behavior for React. This is useful if you have an interactive react child component.
    // In other circumstances we want to be careful about re-renders, like for videos - that will restart the video.
    return this.props.hasDynamicContent === true || !instance || !_.isEqual(instance, nextInstance);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {module:Overlay.Components.Overlay}
   */
  render() {
    // Get the instance.
    const instance = this.props.instances[this.state.instanceId];
    if (instance) {
      // Define top/bottom toolbars.
      const _toolbarTop = this.renderToolbarTop();
      const _toolbarBottom = this.renderToolbarBottom();

      // Define the content.
      const _content = this.renderContent();

      // Define classes for positioning.
      let _classes = 'xOverlay ' + instance.positionMode;

      // If any additional classes have been passed as props, merge them.
      if (this.props.classes) {
        _classes += ' ' + this.props.classes;
      }

      // Define inner styles based on state.
      const _styles = {
        top:       instance.top + 'px',
        opacity:   instance.visible ? 1 : 0,
        maxHeight: 'none',
      };

      return (
        <Modal container={() => document.body} show autoFocus={false} backdrop={false} onEscapeKeyDown={this.handleClose}>
          <div
            ref="overlay"
            className={_classes}
            onClick={this.handleCloseClick}
            data-instance-id={this.state.instanceId}
          >
            <div
              ref="overlayInner"
              className="xOverlayInner xDetailContainer"
              style={_styles}
              onClick={this.handleInnerClick}
            >
              {_toolbarTop}
              {_content}
              {_toolbarBottom}
            </div>
          </div>
        </Modal>
      );
    } else {
      return false;
    }
  }

  /**
   * Renders just the top toolbar element.
   *
   * @return {ReactElement} The top toolbar element.
   */
  renderToolbarTop() {
    // Close elements.
    let _close;
    if (this.props.allowUserClose) {
      _close = (
        <div className="xActionContainer xActionClose hasIcon">
          <HybridButton
            onClick={this.handleCloseClick}
            className="xIconButton xAction xActionCloseOverlay"
            iconClass="xIcon xIconClose"
          />
        </div>
      );
    }

    return <div>{_close}</div>;
  }

  /**
   * Renders just the bottom toolbar element.
   *
   * @return {ReactElement} The bottom toolbar element.
   */
  renderToolbarBottom() {
    // Close elements.
    let _close;
    if (this.props.allowUserClose) {
      _close = (
        <div className="xActionContainer xActionClose hasButton">
          <Button onClick={this.handleCloseClick} className="xButton xAction xActionCloseOverlay">
            Close
          </Button>
        </div>
      );
    }

    return <div>{_close}</div>;
  }

  /**
   * Renders just the content element.
   *
   * @return {ReactElement} The content element.
   */
  renderContent() {
    let _children;
    const _childType = typeof this.props.children;

    switch (_childType) {
      case 'object':
        _children = this.props.childrenHandleClose ? React.Children.map(this.props.children, child => {
          return React.cloneElement(child,
            {
              closeHandler: this.handleClose,
            });
        }) : this.props.children;
        break;

      case 'string':
        _children = <div dangerouslySetInnerHTML={{ __html: this.props.children }} />;
        break;
    }

    return <div className="xOverlayContent">{_children}</div>;
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Event handler methods */

  /**
   * Handler called when the user clicks the close button/icon.
   *
   * @param {Object} event The click event object.
   */
  handleCloseClick(event) {
    event.preventDefault();
    event.stopPropagation();

    this.handleClose();
  }

  /**
   *  Utility method to handle closing the overlay.
   *
   */

  handleClose() {
    // If user is allowed to close overlay.
    if (this.props.allowUserClose) {
      // Dispatch action to close overlay.
      this.props.dispatch.close(this.state.instanceId);

      // If supplied, call close handler.
      if (this.props.closeHandler) {
        this.props.closeHandler();
      }
    }
  }

  /**
   * Handler called when the user clicks anywhere inside the overlay.
   *
   * @param {Object} event The click event object.
   */
  handleInnerClick(event) {
    event.stopPropagation();
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Utility methods */

  /**
   * Utility method to center the overlay vertically on the screen, based on its content dimensions.
   *
   */
  center() {
    // Wait for images to finish loading, then dispatch action to center overlay.
    if (this.refs.overlayInner) {
      imagesLoaded(this.refs.overlayInner, () => {
        this.props.dispatch.center(this.state.instanceId, this.refs.overlayInner);
      });
    }
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {Function} closeHandler The handler to call when overlay close button/icon is clicked.
 * @property {Integer} totalInstances The number of overlay instances rendered on the page.
 * @property {Object} instances Object containing data for each instance, keyed by numeric instance id.
 * @property {Boolean} allowUserClose Boolean indicating if the user is allowed to close the overlay manually.
 * @property {Boolean} hasDynamicContent Allow re-renders for all state changes, including children. Without this, an interactive
 * child react component doesn't re-render to show interactive behavior.
 */
Overlay.propTypes = {
  closeHandler:        PropTypes.func,
  childrenHandleClose: PropTypes.bool,
  totalInstances:      PropTypes.number,
  instances:           PropTypes.object,
  allowUserClose:      PropTypes.bool,
  hasDynamicContent:   PropTypes.bool,
};

/**
 * React default props.
 *
 * @type {Object}
 */
Overlay.defaultProps = {
  allowUserClose:    true,
  hasDynamicContent: false, // for backwards compatibility
};

/**
 * Maps Redux state values to React component props. Called automatically whenever
 * the bound state changes.
 *
 * @private
 *
 * @param  {Object} state The Redux state.
 * @param  {Object} props The current React component props.
 * @return {Object} Updated React component props.
 */
const mapStateToProps = function(state, ownProps) {
  return {
    totalInstances: Selectors.getTotalInstances(state),
    instances:      Selectors.getInstances(state),
  };
};

/**
 * Binds Redux action dispatches to React component props.
 *
 * @private
 *
 * @param  {Function} dispatch Dispatch context.
 * @return {Object} The mapped functions.
 */
const mapDispatchToProps = function(dispatch) {
  return {
    dispatch: {
      open: function(instanceId) {
        dispatch(Actions.open(instanceId));
      },

      close: function(instanceId) {
        dispatch(Actions.close(instanceId));
      },

      center: function(instanceId, overlayInner) {
        dispatch(Actions.center(instanceId, overlayInner));
      },
    },
  };
};

// Generate connected/smart component.
const OverlayConnected = connect(mapStateToProps, mapDispatchToProps)(Overlay);

// Export the connected/smart component as default.
export default OverlayConnected;

// Also export the dumb component (for unit testing, doc preview etc).
export { Overlay as docPreviewExport };
